import apiClient from './apiClient';
import { withActingAs } from './actingAs';
import type { ApiResponse } from '../types';

const API_TYPE = 'api' as const;

export type ExitScope = 'SINGLE' | 'MULTIPLE' | 'BASKET' | 'STRATEGY' | 'ALL';

interface ExitRequest {
  scope: ExitScope;
  position_ids?: string[];
  basket_id?: string;
  strategy_id?: string;
}

/**
 * Deferred-entry item returned by manual_exit_handler when a position's
 * entry claim is in-flight (Phase 5). Frontend surfaces these as a friendly
 * "still placing — try again in a moment" toast instead of counting them as
 * exits placed.
 */
export interface EntryPlacingDeferredItem {
  position_id: string;
  reason: string;
}

interface ExitResponse {
  exits_placed: number;
  total_positions: number;
  entry_placing_deferred?: EntryPlacingDeferredItem[];
  /**
   * Count of OPEN legs whose exit was deferred because their stop-loss claim
   * was mid-flight (or a concurrent writer held the row) at exit time. These
   * legs are still OPEN and clear within seconds — the user should retry.
   * Distinct from `entry_placing_deferred`, which is an unfilled entry claim.
   */
  exits_deferred?: number;
}

/**
 * Append `?acting_as=<id>` when the active session is acting on behalf of a
 * target user. Backend (manual_exit_handler) resolves via resolve_acting_as
 * in allow_admin=False mode — SuperAdmin and AM-with-relation may write on
 * behalf; Admin role gets 403. Self-only callers send no param and hit the
 * unchanged code path so the existing flow is byte-identical.
 */
function exitUrl(): string {
  const qs = withActingAs(new URLSearchParams()).toString();
  return qs ? `/positions/exit?${qs}` : '/positions/exit';
}

class ExitService {
  async exitPositions(request: ExitRequest): Promise<ExitResponse> {
    try {
      const response = await apiClient.post<ApiResponse<ExitResponse>>(
        exitUrl(),
        request,
        undefined,
        API_TYPE
      );

      const wrapped = response as ApiResponse<ExitResponse>;
      if (!wrapped.success || !wrapped.data) {
        throw new Error(wrapped.message || 'Failed to exit positions');
      }
      return wrapped.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to exit positions');
    }
  }

  async exitSingle(positionId: string): Promise<ExitResponse> {
    return this.exitPositions({ scope: 'SINGLE', position_ids: [positionId] });
  }

  async exitBasket(basketId: string): Promise<ExitResponse> {
    return this.exitPositions({ scope: 'BASKET', basket_id: basketId });
  }

  async exitStrategy(strategyId: string): Promise<ExitResponse> {
    return this.exitPositions({ scope: 'STRATEGY', strategy_id: strategyId });
  }

  async exitAll(): Promise<ExitResponse> {
    return this.exitPositions({ scope: 'ALL' });
  }
}

const exitService = new ExitService();
export default exitService;
